import { ArrowDown, ArrowUp, CalendarDays, CircleAlert, CircleCheck, CircleDashed, Circle, ListChecks, SquareKanban, User } from 'lucide-react';
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode, type RefObject } from 'react';
import { usePolling } from '../../hooks/usePolling';
import { useNow } from '../../hooks/useNow';
import { addDays, calendarRange, dateKey } from '../../lib/calendarRange';
import {
  DUE_GROUP_LABELS,
  dueGroup,
  ganttBar,
  ganttRows,
  ganttTicks,
  ganttWindow,
  groupBy,
  initials,
  isOverdue,
  labelColor,
  listOrder,
  localDay,
  personColor,
  shownTasks,
  textOn,
  type DueGroup,
} from '../../lib/planner';
import { ApiError, endpoints, plannerPhotoUrl, type PlannerBoard, type PlannerCard } from '../../services/api';
import { usePlannerTask, type PlannerSource } from '../../store/plannerTask';
import { useSharedSource } from '../../store/screen';
import { startMicrosoft, useSession } from '../../store/session';
import { useSettings, type PlannerPlanRef } from '../../store/settings';

/**
 * Microsoft Planner boards: Kanban, list, Gantt or calendar (Settings → Widgets → Planner).
 * Read-only, refreshed every 2 minutes. Long boards don't scroll – a TV can't – so each
 * column, list and chart shows what fits and says how many more there are.
 *
 * `fill`: the widget has a fixed-size box (Custom layout) and stretches to fill it.
 */
export function Planner({ fill = false }: { fill?: boolean }) {
  const microsoft = useSession((s) => s.microsoft);
  const { plans, rotateSec, textSize } = useSettings((s) => s.planner);
  // Only the Board view needs cards in Planner's own order (it costs Planner a request per task).
  const cardOrder = useSettings((s) => s.planner.view === 'kanban');
  // Someone else's shared canvas with their Planner boards: those boards, through the canvas.
  const shared = useSharedSource('planner');
  const canRead = Boolean(shared) || Boolean(microsoft?.connected);
  const [index, setIndex] = useState(0);
  const active: PlannerPlanRef | undefined = plans[index % Math.max(1, plans.length)];

  // Several plans: move to the next one every `rotateSec` seconds (restarts after a tap on a tab).
  // Paused while a task's details are open.
  const taskOpen = usePlannerTask((s) => s.open !== null);
  useEffect(() => {
    if (plans.length < 2 || rotateSec <= 0 || taskOpen) return;
    const t = window.setInterval(() => setIndex((i) => (i + 1) % plans.length), rotateSec * 1000);
    return () => window.clearInterval(t);
  }, [plans.length, rotateSec, index, taskOpen]);

  const load = useCallback(
    () =>
      endpoints
        .plannerBoard(active!.id, active!.account, shared, cardOrder)
        .then((r) => r.board)
        .catch((e: unknown) => {
          // Microsoft throttles Planner when it's asked too often; the widget tries again shortly.
          if (e instanceof ApiError && e.code === 'rate_limited') throw new Error('Microsoft is limiting Planner requests – trying again shortly.');
          throw e;
        }),
    [active?.id, active?.account, shared, cardOrder], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const board = usePolling(canRead && active ? `planner:${shared ?? ''}:${active.account ?? ''}:${active.id}:${cardOrder ? 'ordered' : 'plain'}` : null, load, 2 * 60_000);

  let body: ReactNode;
  if (!canRead) {
    body = (
      <div className="muted space-y-2 text-sm">
        <p>Connect your work or school Microsoft account in Settings → Account to show Planner boards.</p>
        <button className="text-[var(--accent)] hover:underline" onClick={() => startMicrosoft('link')}>
          Connect Microsoft
        </button>
      </div>
    );
  } else if (!active) {
    body = <p className="muted text-sm">Choose a plan in Settings → Widgets → Planner.</p>;
  } else if (!board.data) {
    body = <p className="muted text-sm">{board.loading ? 'Loading Planner…' : board.error}</p>;
  } else {
    body = (
      <SourceContext.Provider value={{ planId: active.id, account: active.account, canvas: shared }}>
        <BoardView board={board.data} fill={fill} />
      </SourceContext.Provider>
    );
  }

  return (
    <div className={`glass p-4 ${fill ? 'flex h-full flex-col' : ''}`}>
      {/* zoom scales the whole widget, including its rem-sized text, by the chosen text size. */}
      <div className={fill ? 'flex min-h-0 flex-1 flex-col' : ''} style={{ zoom: textSize }}>
        <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1">
          <div className="font-heading flex min-w-0 items-center gap-2 text-sm font-medium uppercase tracking-wider opacity-80">
            <SquareKanban className="h-4 w-4 shrink-0" aria-hidden />
            <span className="truncate">{board.data?.plan.title ?? active?.name ?? 'Planner'}</span>
            {board.data?.stale && board.data.asOf && (
              <span className="shrink-0 text-xs normal-case tracking-normal opacity-70" title="Microsoft is limiting Planner requests – showing the last copy">
                · as of {new Date(board.data.asOf).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </div>
          {plans.length > 1 && (
            <div className="ml-auto flex flex-wrap gap-1" role="tablist" aria-label="Plans">
              {plans.map((p, i) => (
                <button
                  key={`${p.account ?? ''}:${p.id}`}
                  role="tab"
                  aria-selected={p === active}
                  onClick={() => setIndex(i)}
                  className={`rounded-full px-2.5 py-0.5 text-xs transition ${p === active ? 'bg-[var(--accent)] text-black' : 'opacity-70 hover:opacity-100'}`}
                >
                  {p.name}
                </button>
              ))}
            </div>
          )}
        </div>
        {body}
      </div>
    </div>
  );
}

/** Which plan (and account or shared canvas) the board on screen came from – for photos and task details. */
const SourceContext = createContext<PlannerSource | null>(null);

/** Click, or OK on the remote, opens a task's details. */
function useOpenTask(board: PlannerBoard) {
  const source = useContext(SourceContext);
  const show = usePlannerTask((s) => s.show);
  return (t: PlannerCard) => ({
    role: 'button' as const,
    tabIndex: 0,
    'aria-label': `${t.title} – details`,
    onClick: () => source && show(t, board, source),
    onKeyDown: (e: KeyboardEvent) => {
      if ((e.key === 'Enter' || e.key === ' ') && source) {
        e.preventDefault();
        show(t, board, source);
      }
    },
  });
}

// Photos found missing (no photo, or not allowed): initials from then on, without asking again.
const noPhoto = new Set<string>();

/** A person: their photo where there is one, else initials, else a plain icon (name unknown). */
export function Avatar({ id, board, source, size = 'h-5 w-5 text-[0.6rem]' }: { id: string; board: PlannerBoard; source: PlannerSource | null; size?: string }) {
  const name = board.people[id];
  const label = id === board.me ? `You${name ? ` (${name})` : ''}` : (name ?? 'Someone');
  const url = source ? plannerPhotoUrl(source.planId, id, source.account, source.canvas) : null;
  const [failed, setFailed] = useState(() => !url || noPhoto.has(url));
  return (
    <span
      title={label}
      aria-label={label}
      className={`flex shrink-0 items-center justify-center overflow-hidden rounded-full font-semibold text-white ring-2 ring-black/30 ${size}`}
      style={{ background: personColor(id) }}
    >
      {!failed && url ? (
        <img
          src={url}
          alt=""
          className="h-full w-full object-cover"
          onError={() => {
            noPhoto.add(url);
            setFailed(true);
          }}
        />
      ) : name ? (
        initials(name)
      ) : (
        <User className="h-3/5 w-3/5" aria-hidden />
      )}
    </span>
  );
}

function BoardView({ board, fill }: { board: PlannerBoard; fill: boolean }) {
  const view = useSettings((s) => s.planner.view);
  if (view === 'list') return <ListView board={board} fill={fill} />;
  if (view === 'gantt') return <GanttView board={board} fill={fill} />;
  if (view === 'calendar') return <CalendarView board={board} fill={fill} />;
  return <KanbanView board={board} fill={fill} />;
}

// ------------------------------------------------------------------------------ fitting

/**
 * How many of the container's `[data-item]` children are cut off at its bottom edge. The
 * container must be `relative overflow-hidden` with a bounded height.
 */
function useHiddenCount(ref: RefObject<HTMLElement | null>, key: string): number {
  const [hidden, setHidden] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const limit = el.clientHeight + 1;
      let n = 0;
      for (const c of Array.from(el.querySelectorAll<HTMLElement>(':scope > [data-item]'))) if (c.offsetTop + c.offsetHeight > limit) n++;
      setHidden(n);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref, key]);
  return hidden;
}

function More({ count, what = 'more' }: { count: number; what?: string }) {
  if (count <= 0) return null;
  return <div className="muted pt-1 text-xs">+{count} {what}</div>;
}

// --------------------------------------------------------------------------- task parts

function ProgressIcon({ t }: { t: PlannerCard }) {
  if (t.progress === 'completed') return <CircleCheck className="h-4 w-4 shrink-0 text-[var(--accent)]" aria-label="Completed" />;
  if (t.progress === 'inProgress') return <CircleDashed className="h-4 w-4 shrink-0 text-sky-400" aria-label="In progress" />;
  return <Circle className="h-4 w-4 shrink-0 opacity-60" aria-label="Not started" />;
}

function PriorityIcon({ t }: { t: PlannerCard }) {
  if (t.priority === 'urgent') return <CircleAlert className="h-3.5 w-3.5 shrink-0 text-red-400" aria-label="Urgent" />;
  if (t.priority === 'important') return <ArrowUp className="h-3.5 w-3.5 shrink-0 text-amber-300" aria-label="Important" />;
  if (t.priority === 'low') return <ArrowDown className="h-3.5 w-3.5 shrink-0 opacity-60" aria-label="Low priority" />;
  return null;
}

function Labels({ t, board, compact = false }: { t: PlannerCard; board: PlannerBoard; compact?: boolean }) {
  const show = useSettings((s) => s.planner.showLabels);
  if (!show || !t.labels.length) return null;
  if (compact) {
    return (
      <span className="flex shrink-0 gap-0.5">
        {t.labels.map((l) => (
          <span key={l} className="h-2.5 w-2.5 rounded-full" style={{ background: labelColor(l) }} title={board.labels[l]} />
        ))}
      </span>
    );
  }
  return (
    <div className="flex flex-wrap gap-1">
      {t.labels.map((l) => {
        const c = labelColor(l);
        const name = board.labels[l];
        return name ? (
          <span key={l} className="max-w-full truncate rounded px-1.5 py-px text-[0.7rem] font-medium" style={{ background: c, color: textOn(c) }}>
            {name}
          </span>
        ) : (
          <span key={l} className="h-1.5 w-6 rounded-full" style={{ background: c }} aria-hidden />
        );
      })}
    </div>
  );
}

function Assignees({ t, board, max = 3 }: { t: PlannerCard; board: PlannerBoard; max?: number }) {
  const show = useSettings((s) => s.planner.showAssignees);
  const source = useContext(SourceContext);
  if (!show || !t.assignees.length) return null;
  const extra = t.assignees.length - max;
  return (
    <span className="flex shrink-0 -space-x-1.5">
      {t.assignees.slice(0, max).map((id) => (
        <Avatar key={id} id={id} board={board} source={source} />
      ))}
      {extra > 0 && (
        <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-[var(--border)] px-1 text-[0.6rem] font-semibold ring-2 ring-black/30">
          +{extra}
        </span>
      )}
    </span>
  );
}

const fmtDay = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short' });

function Due({ t, now }: { t: PlannerCard; now: Date }) {
  if (!t.due) return null;
  const late = isOverdue(t, now);
  return (
    <span className={`flex shrink-0 items-center gap-1 text-xs tabular-nums ${late ? 'font-medium text-red-400' : 'muted'}`}>
      <CalendarDays className="h-3.5 w-3.5" aria-hidden />
      {fmtDay.format(localDay(t.due))}
      {late && <span className="sr-only">(late)</span>}
    </span>
  );
}

function Checklist({ t }: { t: PlannerCard }) {
  if (!t.checklist) return null;
  return (
    <span className="muted flex shrink-0 items-center gap-1 text-xs tabular-nums" aria-label={`Checklist ${t.checklist.done} of ${t.checklist.total}`}>
      <ListChecks className="h-3.5 w-3.5" aria-hidden />
      {t.checklist.done}/{t.checklist.total}
    </span>
  );
}

// ------------------------------------------------------------------------------- Kanban

function KanbanView({ board, fill }: { board: PlannerBoard; fill: boolean }) {
  const showCompleted = useSettings((s) => s.planner.showCompleted);
  const tasks = shownTasks(board, showCompleted);
  const now = useNow('minute');
  if (!board.buckets.length) return <p className="muted text-sm">This plan has no buckets yet.</p>;
  return (
    <div className={`flex gap-3 overflow-x-auto pb-1 ${fill ? 'min-h-0 flex-1' : ''}`}>
      {board.buckets.map((b) => {
        // Open tasks in Planner order, then the completed ones (as Planner's board shows them).
        const inBucket = tasks.filter((t) => t.bucketId === b.id);
        const ordered = [...inBucket.filter((t) => t.progress !== 'completed'), ...inBucket.filter((t) => t.progress === 'completed')];
        return <KanbanColumn key={b.id} name={b.name} tasks={ordered} board={board} fill={fill} now={now} />;
      })}
    </div>
  );
}

function KanbanColumn({ name, tasks, board, fill, now }: { name: string; tasks: PlannerCard[]; board: PlannerBoard; fill: boolean; now: Date }) {
  const openTask = useOpenTask(board);
  const list = useRef<HTMLUListElement>(null);
  const hidden = useHiddenCount(list, `${tasks.map((t) => `${t.id}${t.progress}`).join(',')}`);
  return (
    <section className="flex min-w-[13rem] max-w-[22rem] flex-1 flex-col rounded-xl bg-black/15 p-2" aria-label={name}>
      <h4 className="mb-2 flex items-center justify-between gap-2 px-1 text-sm font-semibold">
        <span className="truncate">{name}</span>
        <span className="muted text-xs font-normal tabular-nums">{tasks.length}</span>
      </h4>
      <ul ref={list} className={`relative space-y-2 overflow-hidden ${fill ? 'min-h-0 flex-1' : 'max-h-[26rem]'}`}>
        {tasks.map((t) => (
          <li key={t.id} data-item {...openTask(t)} className={`space-y-1.5 rounded-lg border border-[var(--border)] bg-white/5 p-2.5 cursor-pointer outline-none transition hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-[var(--accent)] ${t.progress === 'completed' ? 'opacity-60' : ''}`}>
            <Labels t={t} board={board} />
            <div className="flex items-start gap-2">
              <span className="mt-0.5">
                <ProgressIcon t={t} />
              </span>
              <span className={`line-clamp-3 min-w-0 flex-1 text-sm leading-snug ${t.progress === 'completed' ? 'line-through' : ''}`}>{t.title}</span>
              <PriorityIcon t={t} />
            </div>
            {(t.due || t.checklist || t.assignees.length > 0) && (
              <div className="flex items-center gap-3">
                <Due t={t} now={now} />
                <Checklist t={t} />
                <span className="ml-auto">
                  <Assignees t={t} board={board} />
                </span>
              </div>
            )}
          </li>
        ))}
        {tasks.length === 0 && <li className="muted px-1 text-xs">No tasks</li>}
      </ul>
      <More count={hidden} />
    </section>
  );
}

// --------------------------------------------------------------------------------- List

const DUE_ORDER: DueGroup[] = ['overdue', 'today', 'tomorrow', 'week', 'later', 'none', 'done'];

function ListView({ board, fill }: { board: PlannerBoard; fill: boolean }) {
  const openTask = useOpenTask(board);
  const { showCompleted, groupBy: grouping } = useSettings((s) => s.planner);
  const now = useNow('minute');
  const tasks = listOrder(shownTasks(board, showCompleted));
  const bucketName = new Map(board.buckets.map((b) => [b.id, b.name]));
  const groups =
    grouping === 'bucket'
      ? groupBy(tasks, (t) => (t.bucketId && bucketName.has(t.bucketId) ? t.bucketId : ''), [...board.buckets.map((b) => b.id), '']).map((g) => ({
          label: bucketName.get(g.key) ?? 'Other',
          tasks: g.tasks,
        }))
      : grouping === 'due'
        ? groupBy(tasks, (t) => dueGroup(t, now), DUE_ORDER).map((g) => ({ label: DUE_GROUP_LABELS[g.key], tasks: g.tasks }))
        : [{ label: '', tasks }];
  const list = useRef<HTMLDivElement>(null);
  const hidden = useHiddenCount(list, `${grouping}:${tasks.map((t) => `${t.id}${t.progress}`).join(',')}`);

  if (!tasks.length) return <p className="muted text-sm">{showCompleted ? 'No tasks in this plan.' : 'All done – no open tasks.'}</p>;
  return (
    <div className={fill ? 'flex min-h-0 flex-1 flex-col' : ''}>
      <div ref={list} className={`relative overflow-hidden ${fill ? 'min-h-0 flex-1' : 'max-h-[28rem]'}`}>
        {groups.flatMap((g) => [
          g.label ? (
            <div key={`h-${g.label}`} className={`pb-1 pt-2 text-xs font-semibold uppercase tracking-wider first:pt-0 ${g.label === 'Late' ? 'text-red-400' : 'muted'}`}>
              {g.label}
            </div>
          ) : null,
          ...g.tasks.map((t) => (
            <div key={t.id} data-item {...openTask(t)} className={`flex items-center gap-2 rounded border-b border-[var(--border)]/50 py-1.5 last:border-0 cursor-pointer outline-none transition hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-[var(--accent)] ${t.progress === 'completed' ? 'opacity-60' : ''}`}>
              <ProgressIcon t={t} />
              <span className={`min-w-0 flex-1 truncate text-sm ${t.progress === 'completed' ? 'line-through' : ''}`}>{t.title}</span>
              {grouping !== 'bucket' && t.bucketId && bucketName.get(t.bucketId) && (
                <span className="muted hidden max-w-[8rem] shrink-0 truncate text-xs sm:inline">{bucketName.get(t.bucketId)}</span>
              )}
              <Labels t={t} board={board} compact />
              <PriorityIcon t={t} />
              <Checklist t={t} />
              <Due t={t} now={now} />
              <Assignees t={t} board={board} max={2} />
            </div>
          )),
        ])}
      </div>
      <More count={hidden} what={hidden === 1 ? 'more task' : 'more tasks'} />
    </div>
  );
}

// -------------------------------------------------------------------------------- Gantt

/** Width of the task-name column; the timeline (and its one shared grid) starts after it. */
const GANTT_LABEL = 'clamp(7rem, 32%, 16rem)';

function GanttView({ board, fill }: { board: PlannerBoard; fill: boolean }) {
  const openTask = useOpenTask(board);
  const p = useSettings((s) => s.planner);
  const weekStartsOn = useSettings((s) => s.calendar.weekStartsOn);
  const now = useNow('minute');
  const tasks = shownTasks(board, p.showCompleted);
  const range = ganttWindow(
    tasks,
    { period: p.ganttPeriod, from: p.ganttFrom, to: p.ganttTo, before: p.ganttBefore, after: p.ganttAfter, unit: p.ganttUnit, weekStartsOn },
    now,
  );
  const ticks = ganttTicks(range, weekStartsOn);
  const bars = new Map(tasks.map((t) => [t.id, ganttBar(t, range)]));
  const undated = tasks.filter((t) => !t.start && !t.due).length;
  const outside = tasks.filter((t) => !bars.get(t.id)).length - undated;
  // Earliest start at the top – as one list, or under bucket headings in the board's order.
  const rows = ganttRows(board, tasks, p.ganttGroup, (t) => bars.get(t.id) ?? null);
  const list = useRef<HTMLDivElement>(null);
  const hidden = useHiddenCount(list, `${range.first.getTime()}:${range.days}:${rows.map((r) => r.id).join(',')}`);

  const pct = (days: number) => `${(days / range.days) * 100}%`;
  const todayAt = (now.getTime() - range.first.getTime()) / 86_400_000;
  const labelCol = { width: GANTT_LABEL } as const;

  return (
    <div className={fill ? 'flex min-h-0 flex-1 flex-col' : ''}>
      <div className="flex text-xs">
        <div className="shrink-0" style={labelCol} />
        <div className="relative h-5 flex-1 overflow-hidden">
          {ticks.labels.map((l) => (
            <span key={l.at} className="muted absolute whitespace-nowrap pl-1 tabular-nums" style={{ left: pct(l.at) }}>
              {l.text}
            </span>
          ))}
        </div>
      </div>
      <div ref={list} className={`relative overflow-hidden ${fill ? 'min-h-0 flex-1' : 'max-h-[28rem]'}`}>
        {/* One grid behind every row: weekends, day/week/month lines and today. */}
        <div className="pointer-events-none absolute inset-y-0 right-0" style={{ left: GANTT_LABEL }} aria-hidden>
          {ticks.weekends.map((i) => (
            <div key={`w${i}`} className="absolute inset-y-0 bg-white/[0.04]" style={{ left: pct(i), width: pct(1) }} />
          ))}
          {ticks.lines.map((l) => (
            <div key={`l${l.at}`} className={`absolute inset-y-0 border-l ${l.strong ? 'border-[var(--border)]' : 'border-[var(--border)]/30'}`} style={{ left: pct(l.at) }} />
          ))}
          {todayAt >= 0 && todayAt <= range.days && <div className="absolute inset-y-0 w-0.5 bg-[var(--accent)]" style={{ left: pct(todayAt) }} />}
        </div>
        {rows.map((r) =>
          r.kind === 'bucket' ? (
            <div key={r.id} data-item className="relative flex h-7 items-end">
              <div className="muted shrink-0 truncate pb-0.5 pr-2 text-xs font-semibold uppercase tracking-wider" style={labelCol}>
                {r.name}
              </div>
            </div>
          ) : (
            <div key={r.id} data-item {...openTask(r.t)} className="relative flex h-7 items-center rounded cursor-pointer outline-none transition hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-[var(--accent)]">
              <div className="flex min-w-0 shrink-0 items-center gap-1.5 pr-2" style={labelCol}>
                <ProgressIcon t={r.t} />
                <span className={`truncate text-sm ${r.t.progress === 'completed' ? 'muted line-through' : ''}`}>{r.t.title}</span>
              </div>
              <div className="relative h-full flex-1">
                <GanttMark t={r.t} bar={r.bar} pct={pct} now={now} />
              </div>
            </div>
          ),
        )}
        {!rows.length && <p className="muted relative py-2 text-sm">No tasks with dates in this period.</p>}
      </div>
      <More count={hidden} what={hidden === 1 ? 'more row' : 'more rows'} />
      {(undated > 0 || outside > 0) && (
        <div className="muted pt-1 text-xs">
          {[undated > 0 ? `${undated} without dates` : '', outside > 0 ? `${outside} outside this period` : ''].filter(Boolean).join(' · ')}
        </div>
      )}
    </div>
  );
}

function GanttMark({ t, bar, pct, now }: { t: PlannerCard; bar: NonNullable<ReturnType<typeof ganttBar>>; pct: (d: number) => string; now: Date }) {
  const color = t.labels[0] ? labelColor(t.labels[0]) : 'var(--accent)';
  const done = t.progress === 'completed';
  const late = isOverdue(t, now);
  if (bar.milestone) {
    return (
      <span
        className={`absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rotate-45 ${late ? 'ring-2 ring-red-400' : ''}`}
        style={{ left: pct(bar.from + 0.5), background: color, opacity: done ? 0.45 : 1 }}
        title={t.title}
      />
    );
  }
  return (
    <span
      className={`absolute top-1/2 h-4 -translate-y-1/2 ${bar.clippedStart ? '' : 'rounded-l-full'} ${bar.clippedEnd ? '' : 'rounded-r-full'} ${late ? 'ring-2 ring-red-400' : ''}`}
      style={{
        left: pct(bar.from),
        width: `max(0.5rem, ${pct(bar.to - bar.from)})`,
        background: t.progress === 'inProgress' ? `repeating-linear-gradient(135deg, ${color} 0 6px, transparent 6px 9px), ${color}99` : color,
        opacity: done ? 0.45 : 0.9,
      }}
      title={t.title}
    />
  );
}

// ----------------------------------------------------------------------------- Calendar

/** Month grid with each task on its due date (or its start date when it has no due date). */
function CalendarView({ board, fill }: { board: PlannerBoard; fill: boolean }) {
  const showCompleted = useSettings((s) => s.planner.showCompleted);
  const weekStartsOn = useSettings((s) => s.calendar.weekStartsOn);
  const now = useNow('minute');
  const range = calendarRange('month', 0, weekStartsOn, now);
  const byDay = new Map<string, PlannerCard[]>();
  for (const t of listOrder(shownTasks(board, showCompleted))) {
    const when = t.due ?? t.start;
    if (!when) continue;
    const k = dateKey(localDay(when));
    byDay.set(k, [...(byDay.get(k) ?? []), t]);
  }
  const days = Array.from({ length: range.days }, (_, i) => addDays(range.first, i));
  const weekday = new Intl.DateTimeFormat(undefined, { weekday: 'short' });
  const todayKey = dateKey(now);

  return (
    <div className={fill ? 'flex min-h-0 flex-1 flex-col' : ''}>
      <div className="muted mb-1 text-center text-xs font-medium">
        {new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric' }).format(now)}
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-[0.7rem] uppercase tracking-wider opacity-70">
        {days.slice(0, 7).map((d) => (
          <div key={d.getDay()}>{weekday.format(d)}</div>
        ))}
      </div>
      <div
        className={`mt-1 grid grid-cols-7 gap-1 ${fill ? 'min-h-0 flex-1' : ''}`}
        style={{ gridTemplateRows: `repeat(${range.days / 7}, minmax(${fill ? '0' : '4.5rem'}, 1fr))` }}
      >
        {days.map((d) => (
          <DayCell key={dateKey(d)} day={d} tasks={byDay.get(dateKey(d)) ?? []} inMonth={d.getMonth() === now.getMonth()} isToday={dateKey(d) === todayKey} now={now} board={board} />
        ))}
      </div>
    </div>
  );
}

function DayCell({ day, tasks, inMonth, isToday, now, board }: { day: Date; tasks: PlannerCard[]; inMonth: boolean; isToday: boolean; now: Date; board: PlannerBoard }) {
  const openTask = useOpenTask(board);
  const list = useRef<HTMLUListElement>(null);
  const hidden = useHiddenCount(list, tasks.map((t) => t.id).join(','));
  return (
    <div className={`flex min-h-0 flex-col rounded-md p-1 text-left ${inMonth ? 'bg-white/5' : 'opacity-40'} ${isToday ? 'ring-2 ring-[var(--accent)]' : ''}`}>
      <div className={`text-xs tabular-nums ${isToday ? 'font-bold text-[var(--accent)]' : 'muted'}`}>{day.getDate()}</div>
      <ul ref={list} className="relative min-h-0 flex-1 space-y-0.5 overflow-hidden">
        {tasks.map((t) => {
          const c = t.labels[0] ? labelColor(t.labels[0]) : 'var(--accent)';
          return (
            <li
              key={t.id}
              data-item
              {...openTask(t)}
              className={`truncate rounded border-l-[3px] bg-black/20 px-1 text-[0.7rem] leading-5 cursor-pointer outline-none hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-[var(--accent)] ${t.progress === 'completed' ? 'line-through opacity-60' : ''} ${isOverdue(t, now) ? 'text-red-300' : ''}`}
              style={{ borderColor: c }}
              title={t.title}
            >
              {t.title}
            </li>
          );
        })}
      </ul>
      {hidden > 0 && <div className="muted text-[0.65rem] leading-4">+{hidden} more</div>}
    </div>
  );
}
